<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <!-- 条件搜索 -->
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="48">
            <a-col :md="8" :sm="24">
              <a-form-item :label="$t('views.machine.search.name')" prop="name">
                <a-input v-model="queryParam.name" :placeholder="$t('views.machine.search.namePlaceholder')" allow-clear/>
              </a-form-item>
            </a-col>
            <a-col :md="8" :sm="24">
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery"><a-icon type="search" />{{ $t('views.machine.btn.query') }}</a-button>
                <a-button style="margin-left: 8px" @click="resetQuery"><a-icon type="redo" />{{ $t('views.machine.btn.reset') }}</a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <!-- 操作 -->
      <div class="table-operations">
        <a-button type="primary" @click="$refs.createForm.handleAdd()" v-hasPermi="['user:machine:add']">
          <a-icon type="plus" />{{ $t('views.machine.btn.add') }}
        </a-button>
        <a-button type="primary" :disabled="single" @click="$refs.createForm.handleUpdate(undefined, ids)" v-hasPermi="['user:machine:edit']">
          <a-icon type="edit" />{{ $t('views.machine.btn.edit') }}
        </a-button>
        <a-button type="danger" :disabled="multiple" @click="handleDelete" v-hasPermi="['user:machine:remove']">
          <a-icon type="delete" />{{ $t('views.machine.btn.delete') }}
        </a-button>
        <table-setting
          :style="{float: 'right'}"
          :table-size.sync="tableSize"
          v-model="columns"
          :refresh-loading="loading"
          @refresh="getList" />
      </div>
      <!-- 增加修改 -->
      <create-form
        ref="createForm"
        @ok="getList"
      />
      <!-- 数据展示 -->
      <a-table
        :loading="loading"
        :size="tableSize"
        rowKey="id"
        :columns="columns"
        :data-source="list"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
        :pagination="false"
        :bordered="tableBordered"
        :scroll="{ x: 'max-content' }"
      >
        <!-- 表格排序列已注释
        <span slot="sort" slot-scope="text, record">
          <a-input-number
            :value="record.sort"
            :min="0"
            style="width: 100px"
            @change="(value) => handleSortChange(record, value, 'sort')"
          />
        </span>
        -->
        <span slot="status" slot-scope="text, record">
          <a-switch
            :checked="record.status == 1 || record.status == '1'"
            :disabled="!canEdit"
            @change="(checked) => handleStatusChange(record, checked)"
          />
        </span>
        <span slot="createTime" slot-scope="text, record">
          {{ parseTime(record.createTime) }}
        </span>
        <span slot="operation" slot-scope="text, record">
          <a-divider type="vertical" v-hasPermi="['user:machine:edit']" />
          <a @click="$refs.createForm.handleUpdate(record, undefined)" v-hasPermi="['user:machine:edit']">
            <a-icon type="edit" />{{ $t('views.machine.btn.edit') }}
          </a>
          <a-divider type="vertical" v-hasPermi="['user:machine:remove']" />
          <a @click="handleDelete(record)" v-hasPermi="['user:machine:remove']">
            <a-icon type="delete" />{{ $t('views.machine.btn.delete') }}
          </a>
        </span>
      </a-table>
      <!-- 分页 -->
      <a-pagination
        class="ant-table-pagination"
        show-size-changer
        show-quick-jumper
        :current="queryParam.pageNum"
        :total="total"
        :page-size="queryParam.pageSize"
        :showTotal="paginationShowTotal"
        @showSizeChange="onShowSizeChange"
        @change="changeSize"
      />
    </a-card>
  </page-header-wrapper>
</template>

<script>
import { pageMachine, delMachine, updateMachineSort, updateMachineStatus } from '@/api/user/machine'
import CreateForm from './modules/CreateForm'
import { tableMixin } from '@/store/table-mixin'
import auth from '@/plugins/auth'

export default {
  name: 'Machine',
  components: {
    CreateForm
  },
  mixins: [tableMixin],
  data () {
    return {
      list: [],
      selectedRowKeys: [],
      selectedRows: [],
      single: true,
      multiple: true,
      ids: [],
      loading: false,
      total: 0,
      queryParam: {
        name: null,
        pageNum: 1,
        pageSize: 10
      },
      columns: []
    }
  },
  computed: {
    /** 与接口说明一致：更新状态使用 user:machine:edit */
    canEdit () {
      return auth.hasPermi('user:machine:edit')
    }
  },
  created () {
    this.buildColumns()
    this.getList()
  },
  watch: {
    '$i18n.locale' () {
      this.buildColumns()
    }
  },
  methods: {
    buildColumns () {
      const t = k => this.$t(k)
      this.columns = [
        { title: t('views.machine.column.id'), dataIndex: 'id', ellipsis: true, align: 'center' },
        { title: t('views.machine.column.name'), dataIndex: 'name', ellipsis: true, align: 'center' },
        { title: t('views.machine.column.machineMeter'), dataIndex: 'machineMeter', ellipsis: true, align: 'center' },
        { title: t('views.machine.column.doubleMeter'), dataIndex: 'doubleMeter', ellipsis: true, align: 'center' },
        { title: t('views.machine.column.bottomMeter'), dataIndex: 'bottomMeter', ellipsis: true, align: 'center' },
        // { title: t('views.machine.column.sort'), dataIndex: 'sort', scopedSlots: { customRender: 'sort' }, ellipsis: true, align: 'center' },
        { title: t('views.machine.column.status'), dataIndex: 'status', scopedSlots: { customRender: 'status' }, ellipsis: true, align: 'center' },
        { title: t('views.machine.column.createTime'), dataIndex: 'createTime', scopedSlots: { customRender: 'createTime' }, ellipsis: true, align: 'center' },
        { title: t('views.machine.column.operation'), dataIndex: 'operation', width: 200, fixed: 'right', scopedSlots: { customRender: 'operation' }, align: 'center' }
      ]
    },
    paginationShowTotal (total) {
      return this.$t('views.machine.pagination.total', { total })
    },
    getList () {
      this.loading = true
     pageMachine(this.queryParam).then(response => {
        this.list = response.data.records
        this.total = response.data.total
        this.loading = false
      })
    },
    handleQuery () {
      this.queryParam.pageNum = 1
      this.getList()
    },
    resetQuery () {
      this.queryParam = {
        name: undefined,
        pageNum: 1,
        pageSize: 10
      }
      this.handleQuery()
    },
    onShowSizeChange (current, pageSize) {
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    changeSize (current, pageSize) {
      this.queryParam.pageNum = current
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    onSelectChange (selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
      this.ids = this.selectedRows.map(item => item.id)
      this.single = selectedRowKeys.length !== 1
      this.multiple = !selectedRowKeys.length
    },
    handleDelete (row) {
      var that = this
      const ids = row.id || this.ids
      if(row.id){
        this.ids.push(row.id)
      }
      this.$confirm({
        title: this.$t('views.machine.confirm.deleteTitle'),
        content: this.$t('views.machine.confirm.deleteContent', { ids: ids }),
        onOk () {
          let data = []
          that.ids.map(id => data.push({"id":id}))
          return delMachine(data)
            .then(() => {
              that.onSelectChange([], [])
              that.getList()
              that.$message.success(that.$t('views.machine.msg.deleteSuccess'), 3)
          })
        },
        onCancel () {
          that.ids = []
        }
      })
    },
    handleExport () {
      var that = this
      this.$confirm({
        title: this.$t('views.machine.confirm.exportTitle'),
        content: this.$t('views.machine.confirm.exportContent'),
        onOk () {
          that.download('user/machine/export', {
            ...that.queryParam
          }, that.$t('views.machine.export.filename', { time: new Date().getTime() }))
        },
        onCancel () {}
      })
    },
    handleStatusChange (record, checked) {
      if (!this.canEdit) {
        return
      }
      const statusValue = checked ? 1 : 0
      updateMachineStatus({ id: record.id, status: statusValue }).then(() => {
        record.status = statusValue
        this.$message.success(this.$t('views.machine.msg.statusOk'))
        this.getList()
      }).catch(() => {
        this.$message.error(this.$t('views.machine.msg.statusFail'))
      })
    },
    handleSortChange (record, value, fieldName) {
      if (value === null || value === undefined) {
        return
      }
      updateMachineSort({ id: record.id, sort: value }).then(() => {
        record[fieldName] = value
        this.$message.success(this.$t('views.machine.msg.sortOk'))
        this.getList()
      }).catch(() => {
        this.$message.error(this.$t('views.machine.msg.sortFail'))
      })
    }
  }
}
</script>
